import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client'
import { useFormatPolePrice } from 'components/common/hooks/useFormatPolePrice'
import { usePricingTypes } from 'components/common/hooks/usePricingTypes'
import { useShowToast } from 'components/common/hooks/useShowToast'
import { useVisiblePrices } from './hooks/useVisiblePrices'
import { images } from 'images/image-library'
import { ImageAsset } from 'components/common/image-asset'
import { ReactComponent as StarIcon } from 'images/competitor-pricing/star.svg'
import classNames from 'classnames/bind'
import styles from './MarkerPopup.module.scss'
import { Popup, useMap } from 'react-leaflet'
import { REMOVE_STATION_COMPETITOR_MUTATION } from '../../../../../../CompetitorPricingMutations'
import { BrandChip } from '../../../../brand-monograms'
import { STATUS_COLORS } from '../../utils'

const cx = classNames.bind(styles)

const MarkerPopup = ({
  station,
  validFuelGrades,
  selectedFuelGrade,
  isMyStation,
  stationId,
  stationPolePrices,
  showNearbyStationCompetitors,
}) => {
  const { formatPolePrice } = useFormatPolePrice()
  const { selectedPricingType } = usePricingTypes()
  const { t } = useTranslation(['translations'])
  const { showErrorToast } = useShowToast()
  const map = useMap()

  const [removeCompetitor, { loading: removing }] = useMutation(
    REMOVE_STATION_COMPETITOR_MUTATION,
    {
      variables: { stationId, competitorId: station.id },
      refetchQueries: ['competitorPricingQuery'],
      onCompleted: () => map.closePopup(),
      onError: () => showErrorToast(),
    }
  )

  const priceComparison = useMemo(() => {
    if (isMyStation || !stationPolePrices || !selectedFuelGrade) return null

    const matchTypeToSelected = (pricingType) =>
      selectedPricingType === 'ALL'
        ? pricingType === 'ALL'
        : pricingType === selectedPricingType || pricingType === 'ALL'

    const ownPrice = stationPolePrices.find(
      (polePrice) => polePrice.fuelGradeId === selectedFuelGrade && matchTypeToSelected(polePrice.pricingType)
    )?.price
    const competitorPrice = station.polePrices.find(
      (polePrice) => polePrice.fuelGradeId === selectedFuelGrade && matchTypeToSelected(polePrice.pricingType)
    )?.price

    if (ownPrice == null || competitorPrice == null) return null

    const diff = competitorPrice - ownPrice
    if (diff === 0) return null

    const selectedGrade = validFuelGrades.find((fg) => fg.id === selectedFuelGrade)
    const fuelGradeTitle = selectedGrade?.title || selectedGrade?.shortTitle || ''
    const absDiff = formatPolePrice(Math.abs(diff))
    // Locale-aware price unit (ppl / cpg / cpl), lowercased to sit inline after the number.
    const priceUnit = t('price.short').toLowerCase()
    const statusColor = diff < 0 ? STATUS_COLORS.cheaper : STATUS_COLORS.expensive

    return {
      color: statusColor,
      text:
        diff < 0
          ? t('widgets.competitorPricing.priceBelowYours', {
              diff: absDiff,
              unit: priceUnit,
              grade: fuelGradeTitle,
            })
          : t('widgets.competitorPricing.priceAboveYours', {
              diff: absDiff,
              unit: priceUnit,
              grade: fuelGradeTitle,
            }),
    }
  }, [
    isMyStation,
    stationPolePrices,
    station.polePrices,
    selectedFuelGrade,
    selectedPricingType,
    validFuelGrades,
    formatPolePrice,
    t,
  ])

  const visiblePrices = useVisiblePrices(station, validFuelGrades, selectedFuelGrade)

  return (
    <Popup className={cx('popup-wrapper')}>
      <div className={cx('popup')}>
        <div className={cx('popup-head')}>
          <BrandChip brand={station.brand} size={30} />
          <div className={cx('popup-title')}>
            <div className={cx('popup-name')}>{station.name}</div>
            {!isMyStation && showNearbyStationCompetitors && (
              <div className={cx('popup-sub')}>
                {station.brand?.name} &middot;{' '}
                {t('widgets.competitorPricing.tracked')}
              </div>
            )}
          </div>
          {station.influencer && (
            <StarIcon className={cx('popup-star')} />
          )}
        </div>
        {priceComparison && (
          <div
            className={cx('popup-status')}
            style={{ '--c': priceComparison.color }}
          >
            <span className={cx('dot')} />
            <span>{priceComparison.text}</span>
          </div>
        )}
        <div className={cx('popup-list')}>
          {visiblePrices.map(({ key, gradeColor, gradeName, dateLabel, formattedPrice, source, isSelected }) => (
            <div key={key} className={cx('popup-row', isSelected && 'active')}>
              <span className={cx('popup-grade')} style={{ backgroundColor: gradeColor }}>
                {gradeName}
              </span>
              <div className={cx('popup-info')}>
                <span className={cx('popup-date')}>{dateLabel}</span>
                <span className={cx('popup-price')}>{formattedPrice}</span>
              </div>
              {source && (
                <ImageAsset
                  src={images.polePriceShort(source)}
                  width={18}
                  height={18}
                  alt={`${source}`}
                  fallbackImage={null}
                  className={cx('popup-source')}
                />
              )}
            </div>
          ))}
        </div>
        {showNearbyStationCompetitors && !isMyStation && stationId && (
          <div className={cx('popup-foot')}>
            <button
              className={cx('btn-ghost')}
              onClick={() => removeCompetitor()}
              disabled={removing}
            >
              {removing
                ? '...'
                : t('widgets.competitorPricing.removeFromCompetitorList')}
            </button>
          </div>
        )}
      </div>
    </Popup>
  )
}

MarkerPopup.propTypes = {
  station: PropTypes.shape().isRequired,
  validFuelGrades: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  selectedFuelGrade: PropTypes.string.isRequired,
  isMyStation: PropTypes.bool.isRequired,
  stationId: PropTypes.string,
  stationPolePrices: PropTypes.array,
  showNearbyStationCompetitors: PropTypes.bool,
}

export { MarkerPopup }
